import { Component, computed, effect, inject, Inject, OnDestroy, OnInit, untracked } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { } from 'moment-timezone';
import { combineLatest, of, timer } from 'rxjs';
import { finalize, switchMap, take, tap } from 'rxjs/operators';
import { BasicNotification } from 'src/app/notifications/models/basic-notification';
import { ToastCreationInfo, ToastService } from 'src/app/services/toast.service';
import { AppUser, TrgUserClaims } from 'src/app/shared/models/app-user';
import { AppActions } from 'src/app/state/app';
import { DataDateFormat, DataTimeFormat, LocaleDto, LocaleService, NotificationSectionSettingsDto, NotificationSettingsDto, OrganisationService, UserRole, UserSettingsService } from '../../../services/api.services';
import * as AppSelectors from '../../../state/app/app.selectors';
import { ClonerService } from "../../clone/cloner-service";
import { NotificationToastComponent, NotificationToastDataComponent } from '../../components/toast/notification-toast/notification-toast.component';
import { catchAppError, isNullOrUndefined } from '../../utils';
import { defaultTimeZone, getTimeZoneById, TimeZone, timeZones } from '../../utils/timezone';
import { GenericAlertButton, GenericAlertData, GenericAlertDialog } from '../generic-alert/generic-alert.dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppContext } from 'src/app/app/context';
import { DataFormatter } from '../../../pbir/formatting/DataFormatter';
import { DataType } from '../../../pbir/dataModel/DataType';
import { AppCurrencies } from '../../../state/app/app.reducer';

export interface ManageProfileData {
  tab: number
}

@Component({
  templateUrl: './manage-profile.dialog.html',
  styleUrls: ['./manage-profile.dialog.scss'],
  standalone: false
})
export class ManageProfileDialog implements OnInit, OnDestroy {
  readonly appContext = inject(AppContext);

  userClaims: TrgUserClaims;

  locales = this.appContext.locales;
  selectedLocale: LocaleDto = null;

  timeZones: TimeZone[] = timeZones;
  selectedTimeZone: TimeZone = defaultTimeZone;

  readonly user = this.appContext.user;
  readonly businessUser = computed(() => this.user().role == UserRole.ClientUser);

  notificationsEnabled = false;
  toastViewPortLocation: number;
  sections: NotificationSectionSettingsDto[] = [];
  dirty = false;

  selectedTabIndex = 0;

  previewOpen = false;
  toastPreview: ToastCreationInfo;

  constructor(
    public dialogRef: MatDialogRef<ManageProfileDialog>,
    @Inject(MAT_DIALOG_DATA) public data: ManageProfileData,
    public store: Store,
    public localeService: LocaleService,
    public userSettingsService: UserSettingsService,
    public dialog: MatDialog,
    public organisationService: OrganisationService,
    public toastService: ToastService,
    private clonerService: ClonerService) {
    this.dialogRef.addPanelClass('settings-modal-dialog-panel');

    this.selectedTabIndex = this.data.tab;

    this.store.select(AppSelectors.getUserClaims)
      .pipe(take(1))
      .subscribe(c => this.userClaims = c);

    effect(() => {
      let userLocale = this.appContext.locale();
      let userTz = this.appContext.timeZone();

      this.selectedLocale = untracked(() => this.locales()).find(l => l === userLocale);
      this.selectedTimeZone = userTz;

      if (!this.selectedTimeZone)
        this.selectedTimeZone = defaultTimeZone;
    });

    effect(() => {
      let appSettings = this.appContext.settings();
      this.notificationsEnabled = !!appSettings.notificationSettings?.notificationsEnabled;
      this.toastViewPortLocation = appSettings.notificationSettings?.toastViewPortLocation;
      this.sections = this.clonerService.deepClone(appSettings.notificationSettings?.sections);
    });
  }

  ngOnDestroy(): void {
    this.toastService.restOrientationOverride();
    if(!isNullOrUndefined(this.toastPreview?.index))
      this.toastService.dismiss(this.toastPreview.index, false);
  }

  ngOnInit(): void {
  }

  toastLocationIndexDisplayName(): string {
    switch (this.toastViewPortLocation) {
      case 0: return "Top Right"
      case 1: return "Top Left"
      case 2: return "Bottom Right (Default)"
      case 3: return "Bottom Left"
    }
  }

  previewToast() {
    // Allow preview even when disabled?
    // if (!this.notificationsEnabled || !this.sections[0].toastsEnabled)
    //   return;

    this.toastService.overrideOrientation(this.toastViewPortLocation);

    if (this.previewOpen)
      return;

    let previewNotif = <BasicNotification>{
          id: 0,
          iconUrl: "/assets/img/notification-icons/schedule-success.svg",
          pendingUrl: null,
          color: "#6DD400",
          headline: "Toast Preview",
          title: "Preview Toast",
          message: "This is a Toast Preview",
          buttons: null,
          time: null
    };

    this.toastPreview = this.toastService.open({ component: NotificationToastComponent, data: <NotificationToastDataComponent>{ notification: previewNotif, userCanDismiss: true }, dismissAfter: 5000 })

    this.toastPreview.dismissalSubject.subscribe((info) => {
      this.previewOpen = false;
    })

    this.previewOpen = true;
  }

  mergeNotifSettings(): NotificationSettingsDto {
    return {
      notificationsEnabled: this.notificationsEnabled, 
      toastViewPortLocation: this.toastViewPortLocation,
      sections: this.sections
    }
  }

  save(): void {
    let changed =
         this.selectedLocale.id   !== this.appContext.locale()?.id
      || this.selectedTimeZone.id !== this.appContext.timeZone()?.id
      || this.dirty;
      
    if (changed) {
      this.userSettingsService.addOrUpdate({
        locale: this.selectedLocale.id,
        currency: undefined,
        timeZoneIana: this.selectedTimeZone.id,
        notificationSettings: this.mergeNotifSettings()
      }).pipe(
        switchMap(() => {
          this.close();

          document.body.classList.add('insights-profile-changed');

          timer(800).pipe(
            tap(() => document.body.classList.remove('insights-profile-changed'))
          ).subscribe();

          // Give time for the overlay and dialog to transition
          return timer(300).pipe(tap(() => {
            this.store.dispatch(AppActions.loadSettings());
          }));
        }),
        catchAppError(() => {
          this.close();
        }, { errorMessage: 'Failed to update user profile' })
      ).subscribe();
    } else {
      this.close();
    }
  }

  close(): void {
    this.dialogRef.close();
  }
}
